import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { downloadFile, http } from '@/lib/api/client'
import type {
  CheckoutLinkInfo,
  CustomerDetail,
  CustomerList,
  CustomerNote,
  Resource,
  SavedView,
} from '@/lib/api/types'

/** The customer list's search, filters and sort (kept in the address bar). */
export interface CustomerFilters {
  search?: string
  status?: 'open' | 'closed'
  plan?: string
  billing?: string
  signed_up?: string
  activity?: string
  tag?: string
  manager?: string
  tickets?: string
  unverified?: string
  /** The owner's product-news consent: opted_in or opted_out. */
  marketing?: string
  /** "1" also lists internal accounts, which are otherwise left out. */
  internal?: string
  /** paying, free, new, risk, payment_issue, ending, closing or mine. */
  segment?: string
  /** tier1, hubs or other: the owner's country among the ad markets. */
  market?: string
  /** google, meta, tagged or untagged: where the owner signed up from. */
  channel?: string
  /** good, mid or low. */
  health?: string
  sort?: string
}

export function useCustomers(filters: CustomerFilters, page: number) {
  return useQuery({
    queryKey: ['customers', filters, page],
    queryFn: () => http.get<CustomerList>('/customers', { ...filters, page }),
    placeholderData: keepPreviousData,
  })
}

/** Owners and admins: the filtered list as a CSV (recorded in the audit log). */
export function exportCustomers(filters: CustomerFilters) {
  return downloadFile('/customers/export', { ...filters })
}

function useCustomerUpdate<TInput, TResult>(id: string, request: (input: TInput) => Promise<TResult>) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: request,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['customer', id] })
      void queryClient.invalidateQueries({ queryKey: ['customers'] })
    },
  })
}

/** Tags and the account manager. */
export function useUpdateCrm(id: string) {
  return useCustomerUpdate(id, (input: { tags?: string[]; account_manager_id?: string | null }) =>
    http.patch<Resource<CustomerDetail['crm']>>(`/customers/${id}/crm`, input),
  )
}

export function useAddNote(id: string) {
  return useCustomerUpdate(id, (input: { body: string; pinned?: boolean }) =>
    http.post<Resource<CustomerNote>>(`/customers/${id}/notes`, input),
  )
}

export function useUpdateNote(id: string) {
  return useCustomerUpdate(id, ({ noteId, ...input }: { noteId: string; body?: string; pinned?: boolean }) =>
    http.patch<Resource<CustomerNote>>(`/customers/${id}/notes/${noteId}`, input),
  )
}

export function useDeleteNote(id: string) {
  return useCustomerUpdate(id, (noteId: string) => http.delete(`/customers/${id}/notes/${noteId}`))
}

export interface CheckoutLinkInput {
  kind: 'plan' | 'addon'
  plan?: string
  interval?: 'monthly' | 'annual'
  addon?: string
  quantity?: number
  discount_code?: string
  note?: string
  email: boolean
}

/** A checkout for the customer to pay, emailed to the owner (and shown to copy). */
export function useCreateCheckoutLink(id: string) {
  return useCustomerUpdate(
    id,
    async (input: CheckoutLinkInput) =>
      (await http.post<Resource<CheckoutLinkInfo>>(`/customers/${id}/checkout-links`, input)).data,
  )
}

/** Give the customer top-ups (owners and admins): one-time packs, valid 12 months. */
export function useGrantTopUp(id: string) {
  return useCustomerUpdate(id, (input: { pack: string; quantity: number; note: string }) =>
    http.post(`/customers/${id}/top-ups`, input),
  )
}

/**
 * Lifetime plans (decision 30, owners and admins, all in the staff audit log): attach a paid order
 * whose payment message was missed, revoke with a reason, move once to another workspace of the
 * same owner, the change and closure refunds (both end the plan), and the internal test licence
 * (the owner only, counted in the batch of 100, at most 3 ever).
 */
export function useLifetimeAction(id: string) {
  return useCustomerUpdate(
    id,
    (
      action:
        | { kind: 'attach'; order_id: string; note: string }
        | { kind: 'revoke'; license: string; reason: string }
        | { kind: 'move'; license: string; workspace_id: string; note: string }
        | { kind: 'refund'; license: string; refund: 'change' | 'closure'; note: string }
        | { kind: 'internal'; plan: string; note: string },
    ) => {
      const base = `/customers/${id}/lifetime`
      switch (action.kind) {
        case 'attach':
          return http.post(`${base}/attach`, { order_id: action.order_id, note: action.note })
        case 'revoke':
          return http.post(`${base}/${action.license}/revoke`, { reason: action.reason })
        case 'move':
          return http.post(`${base}/${action.license}/move`, {
            workspace_id: action.workspace_id,
            note: action.note,
          })
        case 'refund':
          return http.post(`${base}/${action.license}/refund`, {
            kind: action.refund,
            note: action.note,
            confirm: true,
          })
        case 'internal':
          return http.post(`${base}/internal`, { plan: action.plan, note: action.note })
      }
    },
  )
}

/** Gives back the AI credits of one charge whose work didn't work, with a reason for the audit log. */
export function useRefundAiCredits(id: string) {
  return useCustomerUpdate(id, (input: { reservation_id: string; reason: string; notify: boolean }) =>
    http.post(`/customers/${id}/ai-credit-refunds`, input),
  )
}

/** Free plan safeguards: limit Free features (never ban) on this workspace, or its owner's network. */
export function useLimitFree(id: string) {
  return useCustomerUpdate(id, (input: { note: string }) => http.post(`/customers/${id}/free-limit`, input))
}

export function useLiftFreeLimit(id: string) {
  return useCustomerUpdate(id, () => http.delete(`/customers/${id}/free-limit`))
}

export function useLimitNetwork(id: string) {
  return useCustomerUpdate(id, (input: { hash: string; note: string }) =>
    http.post<Resource<CustomerDetail['safety']> & { meta: { limited: number } }>(
      `/customers/${id}/network-limits`,
      input,
    ),
  )
}

export function useLiftNetwork(id: string) {
  return useCustomerUpdate(id, (limitId: string) =>
    http.delete<Resource<CustomerDetail['safety']> & { meta: { lifted: number } }>(
      `/customers/${id}/network-limits/${limitId}`,
    ),
  )
}

/** Opening a customer is recorded in the staff audit log. */
export function useCustomer(id: string) {
  return useQuery({
    queryKey: ['customer', id],
    queryFn: async () => (await http.get<Resource<CustomerDetail>>(`/customers/${id}`)).data,
  })
}

export type PlanAction =
  | { action: 'plan'; plan: string; interval: 'monthly' | 'annual' }
  | { action: 'keep-plan' | 'cancel' | 'resume' }
  | { action: 'complimentary'; plan: string | null; duration?: CompDuration; days?: number; notify?: boolean }

export type CompDuration = 'day' | 'week' | 'month' | 'year' | 'custom'

/** Owners and admins: free add-on units that come with a complimentary plan and end with it. */
export function useComplimentaryAddons(id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ addons, notify = true }: { addons: Record<string, number>; notify?: boolean }) =>
      (await http.put<Resource<CustomerDetail>>(`/customers/${id}/complimentary-addons`, { addons, notify }))
        .data,
    onSuccess: (customer) => {
      queryClient.setQueryData(['customer', id], customer)
      void queryClient.invalidateQueries({ queryKey: ['customers'] })
    },
  })
}

/** Owners and admins: change, keep, cancel or resume a customer's plan, or give one. */
export function usePlanAction(id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (a: PlanAction) => {
      const path = `/customers/${id}/${a.action}`
      const response =
        a.action === 'complimentary'
          ? await http.put<Resource<CustomerDetail>>(path, {
              plan: a.plan,
              duration: a.duration,
              days: a.days,
              notify: a.notify ?? true,
            })
          : await http.post<Resource<CustomerDetail>>(
              path,
              a.action === 'plan' ? { plan: a.plan, interval: a.interval } : {},
            )
      return response.data
    },
    onSuccess: (customer) => {
      queryClient.setQueryData(['customer', id], customer)
      void queryClient.invalidateQueries({ queryKey: ['customers'] })
      void queryClient.invalidateQueries({ queryKey: ['subscriptions'] })
    },
  })
}

/** Owners and admins: leave an internal account's payments out of revenue figures, or count them again. */
export function useSetInternal(id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (internal: boolean) =>
      (await http.put<Resource<CustomerDetail>>(`/customers/${id}/internal`, { internal })).data,
    onSuccess: (customer) => {
      queryClient.setQueryData(['customer', id], customer)
      void queryClient.invalidateQueries({ queryKey: ['customers'] })
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] })
      void queryClient.invalidateQueries({ queryKey: ['sales'] })
    },
  })
}

export interface CloseInput {
  confirm_name: string
  close_owner_account: boolean
  note: string
  /** The workspace has a lifetime plan: closing ends it without a refund (confirmed separately). */
  confirm_lifetime?: boolean
}

/** Owners and admins, on the customer's request: close now, deleted for good after 30 days. */
export function useCloseCustomer(id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: CloseInput) =>
      (await http.post<Resource<CustomerDetail>>(`/customers/${id}/close`, input)).data,
    onSuccess: (customer) => {
      queryClient.setQueryData(['customer', id], customer)
      void queryClient.invalidateQueries({ queryKey: ['customers'] })
    },
  })
}

/** The owner asked support to change their sign-in email: a link goes to their current address. */
export function useSendOwnerEmailChange(id: string) {
  return useMutation({
    mutationFn: () => http.post<{ message: string }>(`/customers/${id}/owner-email-change`),
  })
}

/** Someone lost their phone and recovery codes: turn off their two-step verification (owners and admins). */
export function useResetTwoFactor(id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (userId: string) =>
      http.post<{ message: string }>(`/customers/${id}/members/${userId}/two-factor-reset`),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['customer', id] }),
  })
}

/** Within the 30 days: everything comes back as it was. */
export function useReopenCustomer(id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async () => (await http.post<Resource<CustomerDetail>>(`/customers/${id}/reopen`)).data,
    onSuccess: (customer) => {
      queryClient.setQueryData(['customer', id], customer)
      void queryClient.invalidateQueries({ queryKey: ['customers'] })
    },
  })
}

/** The staff member's own saved views of the customer list. */
export function useSavedViews() {
  return useQuery({
    queryKey: ['saved-views', 'customers'],
    queryFn: async () => (await http.get<{ data: SavedView[] }>('/saved-views', { page: 'customers' })).data,
  })
}

export function useSaveView() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: { name: string; filters: Record<string, string> }) =>
      http.post<Resource<SavedView>>('/saved-views', { page: 'customers', ...input }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['saved-views'] }),
  })
}

export function useDeleteView() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => http.delete(`/saved-views/${id}`),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['saved-views'] }),
  })
}

/** Add a tag to, or set the account manager of, several customers at once. */
export function useBulkCustomers() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: { ids: string[]; add_tag?: string; account_manager_id?: string | null }) =>
      http.post<Resource<{ updated: number }>>('/customers/bulk', input),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['customers'] }),
  })
}
